/**
 * DefencIQ Scoring Engine
 * Implements exact scoring rules as specified:
 * - weighted_score = (score / 2) * weight
 * - domain_score_percent = (sum(weighted_score) / sum(max_score)) * 100
 * - overall_score = sum(domain_score_percent x domain_weight_percent)
 * - Gating rules control overall status caps
 */

import {
  DOMAIN_WEIGHTS,
  READINESS_BANDS,
  DOMAIN_MATURITY_BANDS,
  CRITICAL_GATING_QUESTIONS,
  COMPONENT_LABELS,
  LAYER_LABELS
} from '@/config/domain-weights';
import {
  QUESTION_CONFIG,
  getQuestionByFieldId,
  getGatingQuestions,
  SCORED_SECTIONS
} from '@/config/question-config';
import { STACK_COMPONENT_MAP, STACK_STATUS_SCORES, DISPLAY_STATUS_MAP, DISPLAY_STATUS_CONFIG, REQUIREMENT_TEXT } from '@/config/stack-component-map';

/**
 * Convert status to score (0, 1, 2)
 * @param {string} status - Implementation status
 * @param {string} type - Question type (status or statusReverse)
 * @returns {number} Score 0, 1, or 2
 */
export const statusToScore = (status, type = 'status') => {
  if (type === 'statusReverse') {
    // For reverse questions, "yes" is bad, "no" is good
    switch (status) {
      case 'yes': return 0;
      case 'partial': return 1;
      case 'no': return 2;
      case 'not_applicable': return 2;
      default: return 0;
    }
  }
  // Normal status
  switch (status) {
    case 'yes': return 2;
    case 'partial': return 1;
    case 'no': return 0;
    case 'not_applicable': return 2;
    default: return 0;
  }
};

/**
 * Get score from an answer based on question config
 * @param {Object} answer - The answer object { status, score, answer }
 * @param {Object} question - Question config
 * @returns {number} Score 0, 1, or 2
 */
export const getAnswerScore = (answer, question) => {
  if (!answer) return 0;
  
  // If score is explicitly set, use it
  if (answer.score !== undefined && answer.score !== null) {
    return answer.score;
  }
  
  // For multi_select_matrix questions, derive from aggregate percentage
  if (question.type === 'multi_select_matrix' && answer.aggregatePercentage !== undefined) {
    const pct = answer.aggregatePercentage;
    return pct >= 85 ? 2 : pct >= 50 ? 1 : 0;
  }
  
  // For selectScored questions, derive from the selected option
  if (question.type === 'selectScored' && answer.answer && question.options) {
    const selectedOption = question.options.find(o => o.value === answer.answer);
    return selectedOption?.score ?? 0;
  }
  
  // For scored multiselect questions, score based on selection quality
  if (question.type === 'multiselect' && question.weight > 0 && answer.answer) {
    const selections = Array.isArray(answer.answer) ? answer.answer : [];
    if (selections.length === 0) return 0;
    const hasSpecific = selections.some(v => v !== 'unknown');
    return hasSpecific ? 2 : 0;
  }
  
  // For status questions, derive from status
  if ((question.type === 'status' || question.type === 'statusReverse') && answer.status) {
    return statusToScore(answer.status, question.type);
  }
  
  return 0;
};

/**
 * Calculate weighted score for a single question
 * Formula: weighted_score = (score / 2) * weight
 * @param {number} score - Raw score (0, 1, or 2)
 * @param {number} weight - Question weight
 * @returns {number} Weighted score
 */
export const calculateWeightedScore = (score, weight) => {
  return (score / 2) * weight;
};

/**
 * Calculate domain score percentage
 * Formula: domain_score_percent = (sum(weighted_score) / sum(max_score)) * 100
 * @param {string} sectionId - Section/domain ID
 * @param {Object} answers - All answers keyed by field_id
 * @returns {Object} Domain score details
 */
export const calculateDomainScore = (sectionId, answers) => {
  const section = QUESTION_CONFIG[sectionId];
  if (!section) {
    return { score: 0, maxScore: 0, percentage: 0, answered: 0, total: 0, questions: [] };
  }

  let totalWeightedScore = 0;
  let totalMaxScore = 0;
  let answered = 0;
  const questionScores = [];

  section.questions.forEach(question => {
    // Skip non-scored questions (weight = 0)
    if (question.weight === 0) return;

    const answer = answers[question.field_id];
    const score = getAnswerScore(answer, question);
    const weightedScore = calculateWeightedScore(score, question.weight);
    const maxScore = question.weight; // Max weighted score = (2/2) * weight = weight

    totalWeightedScore += weightedScore;
    totalMaxScore += maxScore;

    if (answer && (answer.status || answer.answer !== undefined)) {
      answered++;
    }

    questionScores.push({
      question_id: question.question_id,
      field_id: question.field_id,
      score,
      weightedScore,
      maxScore,
      criticality: question.criticality,
      gating_rule: question.gating_rule
    });
  });

  const percentage = totalMaxScore > 0 
    ? Math.round((totalWeightedScore / totalMaxScore) * 100) 
    : 0;

  return {
    sectionId,
    score: totalWeightedScore,
    maxScore: totalMaxScore,
    percentage,
    answered,
    total: section.questions.filter(q => q.weight > 0).length,
    questions: questionScores
  };
};

/**
 * Calculate all domain scores
 * @param {Object} answers - All answers keyed by field_id
 * @returns {Object} Domain scores keyed by section ID
 */
export const calculateAllDomainScores = (answers) => {
  const scores = {};
  SCORED_SECTIONS.forEach(sectionId => {
    scores[sectionId] = calculateDomainScore(sectionId, answers);
  });
  return scores;
};

/**
 * Calculate overall ML2 readiness score
 * Formula: overall_score = sum(domain_score_percent x domain_weight_percent)
 * @param {Object} domainScores - All domain scores
 * @returns {number} Overall percentage score (0-100)
 */
export const calculateOverallScore = (domainScores) => {
  let weightedSum = 0;
  let totalWeight = 0;

  Object.entries(DOMAIN_WEIGHTS).forEach(([domainId, weight]) => {
    const domainScore = domainScores[domainId];
    if (domainScore) {
      weightedSum += domainScore.percentage * weight;
      totalWeight += weight * 100;
    }
  });

  return totalWeight > 0 ? Math.round((weightedSum / totalWeight) * 100) : 0;
};

/**
 * Get gating control status
 * Critical gating questions that control overall status
 * @param {Object} answers - All answers keyed by field_id
 * @returns {Object} Gating status with controls and summary
 */
export const getGatingControlStatus = (answers) => {
  const controls = [];
  let score0Count = 0; // Critical fail
  let score1Count = 0; // Partial
  let score2Count = 0; // Pass

  CRITICAL_GATING_QUESTIONS.forEach(fieldId => {
    const question = getQuestionByFieldId(fieldId);
    if (!question) return;

    const answer = answers[fieldId];
    const score = getAnswerScore(answer, question);

    controls.push({
      field_id: fieldId,
      question_id: question.question_id,
      prompt: question.prompt,
      section_id: question.section_id,
      score,
      status: answer?.status || 'unknown',
      passed: score === 2
    });

    if (score === 0) score0Count++;
    else if (score === 1) score1Count++;
    else if (score === 2) score2Count++;
  });

  return {
    controls,
    score0Count,
    score1Count,
    score2Count,
    total: controls.length,
    allPassed: score2Count === controls.length
  };
};

/**
 * Determine overall status based on score and gating rules
 * Gating rules:
 * - If any critical gating question has score = 0, overall_status cannot exceed partially_ready
 * - If 3 or more critical gating questions have score = 0, overall_status = not_ready
 * - If all critical gating questions are at least 1, but any are still 1, overall_status cannot exceed substantially_ready
 * - Only if all critical gating questions = 2 can status be ready_for_validation
 * @param {number} overallScore - Overall percentage score
 * @param {Object} gatingStatus - Gating control status
 * @returns {string} Overall status
 */
export const determineOverallStatus = (overallScore, gatingStatus) => {
  // First determine status based on score bands
  let baseStatus = 'not_ready';
  if (overallScore >= READINESS_BANDS.ready_for_validation.min) {
    baseStatus = 'ready_for_validation';
  } else if (overallScore >= READINESS_BANDS.substantially_ready.min) {
    baseStatus = 'substantially_ready';
  } else if (overallScore >= READINESS_BANDS.partially_ready.min) {
    baseStatus = 'partially_ready';
  }

  // Apply gating rules
  const { score0Count, score1Count, score2Count, total } = gatingStatus;

  // Rule: If 3 or more critical gating questions have score = 0, overall_status = not_ready
  if (score0Count >= 3) {
    return 'not_ready';
  }

  // Rule: If any critical gating question has score = 0, cannot exceed partially_ready
  if (score0Count > 0) {
    if (baseStatus === 'ready_for_validation' || baseStatus === 'substantially_ready') {
      return 'partially_ready';
    }
    return baseStatus;
  }

  // Rule: If all are at least 1 but any are 1, cannot exceed substantially_ready
  if (score1Count > 0) {
    if (baseStatus === 'ready_for_validation') {
      return 'substantially_ready';
    }
    return baseStatus;
  }

  // Rule: Only if all critical gating questions = 2 can status be ready_for_validation
  if (score2Count === total && total > 0) {
    return baseStatus; // Can be ready_for_validation
  }

  return baseStatus;
};

/**
 * Get domain maturity band
 * @param {number} percentage - Domain score percentage
 * @returns {string} Maturity band (poor, developing, moderate, strong)
 */
export const getDomainMaturityBand = (percentage) => {
  for (const [band, [min, max]] of Object.entries(DOMAIN_MATURITY_BANDS)) {
    if (percentage >= min && percentage <= max) {
      return band;
    }
  }
  return 'poor';
};

/**
 * Get readiness band label
 * @param {string} status - Overall status
 * @returns {Object} Band info with label and color
 */
export const getReadinessBandInfo = (status) => {
  const bands = {
    not_ready: { label: 'Not Ready', color: 'rose' },
    partially_ready: { label: 'Partially Ready', color: 'amber' },
    substantially_ready: { label: 'Substantially Ready', color: 'sky' },
    ready_for_validation: { label: 'Ready for Validation', color: 'emerald' }
  };
  return bands[status] || bands.not_ready;
};

// ============================================================================
// ENHANCED SCORING ENGINE - Section Groups & Multipliers
// ============================================================================

/**
 * Section Group Definitions
 * CES = Control Effectiveness Score (technical controls)
 * PGS = Policy & Governance Score
 * CMS = Compliance Maturity Score
 */
export const SECTION_GROUPS = {
  CES: {
    name: 'Control Effectiveness Score',
    shortName: 'CES',
    weight: 0.50,
    sections: [
      'identity_and_mfa',
      'endpoint_management',
      'application_control',
      'patch_management',
      'privileged_access',
      'office_macros_and_user_hardening',
      'backups',
      'logging_and_monitoring',
      'vulnerability_management'
    ]
  },
  PGS: {
    name: 'Policy & Governance Score',
    shortName: 'PGS',
    weight: 0.30,
    sections: ['policy_governance', 'personnel_security']
  },
  CMS: {
    name: 'Compliance Maturity Score',
    shortName: 'CMS',
    weight: 0.20,
    sections: ['compliance_and_governance']
  }
};

/**
 * Enhanced Score Bands (for final weighted score)
 */
export const ENHANCED_SCORE_BANDS = [
  { min: 85, max: 100, label: 'ML2 Ready', color: 'emerald', colorClass: 'bg-emerald-500' },
  { min: 70, max: 84, label: 'Near Ready', color: 'yellow', colorClass: 'bg-yellow-500' },
  { min: 50, max: 69, label: 'Developing', color: 'orange', colorClass: 'bg-orange-500' },
  { min: 30, max: 49, label: 'High Risk', color: 'red', colorClass: 'bg-red-500' },
  { min: 0, max: 29, label: 'Critical', color: 'rose', colorClass: 'bg-rose-700' }
];

/**
 * Policy Enforcement Multiplier Mapping (pol_04)
 */
const POLICY_ENFORCEMENT_MULTIPLIERS = {
  'fully_enforced': 1.00,
  'partially_enforced': 0.90,
  'mostly_manual': 0.75,
  'not_enforced': 0.55,
  'unknown': 0.80
};

/**
 * Evidence Multiplier Mapping (pol_05)
 */
const EVIDENCE_MULTIPLIERS = {
  'yes_centralised': 1.00,
  'yes_scattered': 0.88,
  'limited_evidence': 0.72,
  'no_evidence': 0.50,
  'unknown': 0.80
};

/**
 * Calculate group score (average of section percentages within a group)
 * @param {string} groupKey - CES, PGS, or CMS
 * @param {Object} domainScores - All domain scores
 * @returns {number} Group percentage score (0-100)
 */
export const calculateGroupScore = (groupKey, domainScores) => {
  const group = SECTION_GROUPS[groupKey];
  if (!group) return 0;

  let totalPercentage = 0;
  let validSections = 0;

  group.sections.forEach(sectionId => {
    const score = domainScores[sectionId];
    if (score && score.maxScore > 0) {
      totalPercentage += score.percentage;
      validSections++;
    }
  });

  return validSections > 0 ? Math.round(totalPercentage / validSections) : 0;
};

/**
 * Calculate all group scores
 * @param {Object} domainScores - All domain scores
 * @returns {Object} Group scores { CES, PGS, CMS }
 */
export const calculateAllGroupScores = (domainScores) => {
  return {
    CES: calculateGroupScore('CES', domainScores),
    PGS: calculateGroupScore('PGS', domainScores),
    CMS: calculateGroupScore('CMS', domainScores)
  };
};

/**
 * Calculate base score using group weights
 * baseScore = (CES * 0.50) + (PGS * 0.30) + (CMS * 0.20)
 * @param {Object} groupScores - { CES, PGS, CMS }
 * @returns {number} Base score (0-100)
 */
export const calculateBaseScore = (groupScores) => {
  const { CES, PGS, CMS } = groupScores;
  return (CES * SECTION_GROUPS.CES.weight) + 
         (PGS * SECTION_GROUPS.PGS.weight) + 
         (CMS * SECTION_GROUPS.CMS.weight);
};

/**
 * Get policy enforcement multiplier from pol_04 answer
 * @param {Object} answers - All answers
 * @returns {number} Multiplier (0.55-1.0)
 */
export const getPolicyEnforcementMultiplier = (answers) => {
  const answer = answers?.pol_04;
  if (!answer || !answer.answer) return 1.0; // Default if not answered
  
  const value = answer.answer;
  return POLICY_ENFORCEMENT_MULTIPLIERS[value] ?? 1.0;
};

/**
 * Get evidence multiplier from pol_05 answer
 * @param {Object} answers - All answers
 * @returns {number} Multiplier (0.5-1.0)
 */
export const getEvidenceMultiplier = (answers) => {
  const answer = answers?.pol_05;
  if (!answer || !answer.answer) return 1.0; // Default if not answered
  
  const value = answer.answer;
  return EVIDENCE_MULTIPLIERS[value] ?? 1.0;
};

/**
 * Count unknown answers across all sections
 * @param {Object} answers - All answers
 * @returns {number} Count of unknown answers
 */
export const countUnknownAnswers = (answers) => {
  let unknownCount = 0;
  
  Object.values(answers || {}).forEach(answer => {
    if (!answer) return;
    
    // Check various answer formats for "unknown"
    const answerValue = answer.answer || answer.value || answer.status;
    if (typeof answerValue === 'string' && answerValue.toLowerCase() === 'unknown') {
      unknownCount++;
    }
    // Check for unknown in multi-select arrays
    if (Array.isArray(answerValue) && answerValue.includes('unknown')) {
      unknownCount++;
    }
  });
  
  return unknownCount;
};

/**
 * Get confidence multiplier based on unknown answer count
 * @param {number} unknownCount - Number of unknown answers
 * @returns {number} Multiplier (0.85-1.0)
 */
export const getConfidenceMultiplier = (unknownCount) => {
  if (unknownCount >= 5) return 0.85;
  if (unknownCount >= 3) return 0.92;
  if (unknownCount >= 1) return 0.98;
  return 1.0;
};

/**
 * Detect scoring flags based on conditions
 * @param {Object} groupScores - { CES, PGS, CMS }
 * @param {Object} answers - All answers
 * @returns {Array} Array of triggered flags
 */
export const detectScoringFlags = (groupScores, answers) => {
  const flags = [];
  const { CES, PGS } = groupScores;

  // Flag 1: False Readiness
  // Condition: CES >= 75 AND PGS < 50
  if (CES >= 75 && PGS < 50) {
    flags.push({
      id: 'false_readiness',
      label: 'False Readiness Detected',
      severity: 'critical',
      description: 'Strong technical controls but weak policy governance may not withstand audit scrutiny.'
    });
  }

  // Flag 2: Weak Auditability
  // Condition: pol_05 answer is "limited_evidence" or worse
  const evidenceAnswer = answers?.pol_05?.answer;
  const weakEvidenceValues = ['limited_evidence', 'no_evidence', 'unknown'];
  if (evidenceAnswer && weakEvidenceValues.includes(evidenceAnswer)) {
    flags.push({
      id: 'weak_auditability',
      label: 'Weak Auditability',
      severity: 'high',
      description: 'Insufficient evidence collection may impede compliance audits.'
    });
  }

  // Flag 3: Policy Governance Risk
  // Condition: PGS < 60
  if (PGS < 60) {
    flags.push({
      id: 'policy_gap_risk',
      label: 'Policy Governance Risk',
      severity: 'medium',
      description: 'Policy documentation and governance needs improvement for ML2 compliance.'
    });
  }

  return flags;
};

/**
 * Get enhanced score band based on final score
 * @param {number} score - Final score (0-100)
 * @returns {Object} Band info { label, color, colorClass }
 */
export const getEnhancedScoreBand = (score) => {
  const clampedScore = Math.max(0, Math.min(100, score));
  
  for (const band of ENHANCED_SCORE_BANDS) {
    if (clampedScore >= band.min && clampedScore <= band.max) {
      return band;
    }
  }
  
  return ENHANCED_SCORE_BANDS[ENHANCED_SCORE_BANDS.length - 1]; // Default to Critical
};

/**
 * Audit Failure Likelihood Output Messages
 */
export const AUDIT_LIKELIHOOD_MESSAGES = {
  LOW: "Your organisation is well positioned to meet Essential Eight ML2 and DISP requirements. Minor improvements may be required, but overall audit readiness is strong.",
  MODERATE: "Your organisation has a solid foundation, however gaps in governance, enforcement, or evidence may impact audit outcomes. Targeted uplift is recommended prior to DISP onboarding.",
  HIGH: "Your organisation is unlikely to meet Essential Eight ML2 requirements in its current state. Significant gaps in governance, enforcement, and/or audit evidence are present.",
  CRITICAL: "Your organisation is at high risk of failing DISP onboarding and Essential Eight ML2 assessment. Fundamental control, governance, and evidence gaps must be addressed."
};

/**
 * Calculate Audit Failure Likelihood
 * Evaluates conditions in priority order (top-down, first match applies)
 * @param {number} finalScore - Final weighted score (0-100)
 * @param {Object} groupScores - { CES, PGS, CMS }
 * @param {Object} multipliers - { policyEnforcement, evidence, confidence }
 * @param {Array} flags - Array of triggered flags
 * @returns {Object} { likelihood, reasons, message }
 */
export const calculateAuditFailureLikelihood = (finalScore, groupScores, multipliers, flags) => {
  const { PGS } = groupScores;
  const { policyEnforcement, evidence } = multipliers;
  const reasons = [];
  let likelihood = 'MODERATE'; // Default
  
  // Helper to check if flag is triggered
  const hasFlag = (flagId) => flags.some(f => f.id === flagId);
  
  // ===== CRITICAL RISK (Priority 1) =====
  // IF ANY of: finalScore < 40, evidenceMultiplier <= 0.5, policyEnforcementMultiplier <= 0.55
  if (finalScore < 40) {
    likelihood = 'CRITICAL';
    reasons.push("Low overall Essential Eight maturity");
  }
  if (evidence <= 0.5) {
    likelihood = 'CRITICAL';
    reasons.push("Lack of audit-ready evidence");
  }
  if (policyEnforcement <= 0.55) {
    likelihood = 'CRITICAL';
    reasons.push("Insufficient policy enforcement");
  }
  
  // If CRITICAL already determined, return early
  if (likelihood === 'CRITICAL') {
    // Add any additional context reasons
    if (PGS < 30) {
      reasons.push("Fundamental governance gaps present");
    }
    return {
      likelihood,
      reasons: [...new Set(reasons)], // Remove duplicates
      message: AUDIT_LIKELIHOOD_MESSAGES.CRITICAL
    };
  }
  
  // ===== HIGH RISK (Priority 2) =====
  // IF ANY of: finalScore >= 40 AND < 60, PGS < 50, flag: false_readiness, flag: weak_auditability
  if (finalScore >= 40 && finalScore < 60) {
    likelihood = 'HIGH';
    reasons.push("Low overall Essential Eight maturity");
  }
  if (PGS < 50) {
    likelihood = 'HIGH';
    reasons.push("Technical controls not supported by governance");
  }
  if (hasFlag('false_readiness')) {
    likelihood = 'HIGH';
    reasons.push("Technical controls not supported by governance");
    reasons.push("High reliance on manual processes");
  }
  if (hasFlag('weak_auditability')) {
    likelihood = 'HIGH';
    reasons.push("Limited compliance validation mechanisms");
    reasons.push("Lack of audit-ready evidence");
  }
  
  // If HIGH already determined, return early
  if (likelihood === 'HIGH') {
    return {
      likelihood,
      reasons: [...new Set(reasons)],
      message: AUDIT_LIKELIHOOD_MESSAGES.HIGH
    };
  }
  
  // ===== MODERATE RISK (Priority 3) =====
  // IF ANY of: finalScore >= 60 AND < 75, PGS between 50 and 70, evidenceMultiplier < 0.85
  if (finalScore >= 60 && finalScore < 75) {
    likelihood = 'MODERATE';
    reasons.push("Room for improvement in overall maturity");
  }
  if (PGS >= 50 && PGS < 70) {
    likelihood = 'MODERATE';
    reasons.push("Policy governance requires strengthening");
  }
  if (evidence < 0.85) {
    likelihood = 'MODERATE';
    reasons.push("Evidence collection processes need improvement");
  }
  
  // If MODERATE determined with reasons, return
  if (likelihood === 'MODERATE' && reasons.length > 0) {
    return {
      likelihood,
      reasons: [...new Set(reasons)],
      message: AUDIT_LIKELIHOOD_MESSAGES.MODERATE
    };
  }
  
  // ===== LOW RISK (Priority 4) =====
  // IF ALL of: finalScore >= 75, PGS >= 70, evidenceMultiplier >= 0.85, policyEnforcementMultiplier >= 0.90
  if (finalScore >= 75 && PGS >= 70 && evidence >= 0.85 && policyEnforcement >= 0.90) {
    likelihood = 'LOW';
    reasons.push("Strong overall security posture");
    reasons.push("Governance and enforcement well established");
    return {
      likelihood,
      reasons,
      message: AUDIT_LIKELIHOOD_MESSAGES.LOW
    };
  }
  
  // Default fallback - ensure reasons array is never empty
  if (reasons.length === 0) {
    reasons.push("Assessment incomplete or gaps identified");
  }
  
  return {
    likelihood,
    reasons: [...new Set(reasons)],
    message: AUDIT_LIKELIHOOD_MESSAGES[likelihood]
  };
};

/**
 * Calculate enhanced assessment scores with multipliers
 * This layers on top of the existing scoring system
 * @param {Object} answers - All answers keyed by field_id
 * @returns {Object} Enhanced scoring results
 */
export const calculateEnhancedScores = (answers) => {
  // Get existing domain scores
  const domainScores = calculateAllDomainScores(answers);
  
  // Calculate group scores
  const groupScores = calculateAllGroupScores(domainScores);
  
  // Calculate base score
  const baseScore = calculateBaseScore(groupScores);
  
  // Get multipliers
  const policyEnforcementMultiplier = getPolicyEnforcementMultiplier(answers);
  const evidenceMultiplier = getEvidenceMultiplier(answers);
  const unknownCount = countUnknownAnswers(answers);
  const confidenceMultiplier = getConfidenceMultiplier(unknownCount);
  
  // Calculate final score with multipliers
  let finalScore = baseScore * policyEnforcementMultiplier * evidenceMultiplier * confidenceMultiplier;
  
  // Clamp between 0 and 100
  finalScore = Math.max(0, Math.min(100, Math.round(finalScore)));
  
  // Detect flags
  const flags = detectScoringFlags(groupScores, answers);
  
  // Get score band
  const scoreBand = getEnhancedScoreBand(finalScore);
  
  // Calculate Audit Failure Likelihood
  const auditLikelihood = calculateAuditFailureLikelihood(
    finalScore,
    groupScores,
    { policyEnforcement: policyEnforcementMultiplier, evidence: evidenceMultiplier, confidence: confidenceMultiplier },
    flags
  );
  
  return {
    groupScores,
    baseScore: Math.round(baseScore),
    multipliers: {
      policyEnforcement: policyEnforcementMultiplier,
      evidence: evidenceMultiplier,
      confidence: confidenceMultiplier,
      unknownCount
    },
    finalScore,
    scoreBand,
    flags,
    hasCriticalFlags: flags.some(f => f.severity === 'critical'),
    // Audit Failure Likelihood
    auditFailureLikelihood: auditLikelihood.likelihood,
    auditFailureReasons: auditLikelihood.reasons,
    auditFailureMessage: auditLikelihood.message
  };
};

// ============================================================================
// FOCI GATING ENGINE
// ============================================================================

/**
 * Evaluate FOCI (Foreign Ownership, Control & Influence) gate
 * Determines whether foreign involvement poses a readiness risk
 * Scores clarity/completeness - does NOT penalise foreign ownership itself
 * @param {Object} answers - All answers keyed by field_id
 * @returns {Object} { status: 'pass'|'review_required'|'fail', reason: string }
 */
export const evaluateFOCIGate = (answers) => {
  const get = (id) => answers[id]?.answer;

  const riskAnswers = {
    ownership: get('foci_01_foreign_ownership'),
    influence: get('foci_02_foreign_influence'),
    entities20: get('foci_03_foreign_entities_20'),
    entities40: get('foci_04_foreign_entities_40'),
    control: get('foci_05_foreign_control'),
    arrangements: get('foci_06_foreign_arrangements'),
  };
  const complexity = get('foci_07_complexity');

  const values = Object.values(riskAnswers);
  const yesCount = values.filter(v => v === 'yes').length;
  const unsureCount = values.filter(v => v === 'unsure').length;
  const allNo = values.every(v => v === 'no');
  const allAnswered = values.every(v => v && v !== '') && complexity;
  const hasContext = complexity === 'simple' || complexity === 'moderate';
  const highRiskYes = riskAnswers.entities20 === 'yes' ||
                      riskAnswers.entities40 === 'yes' ||
                      riskAnswers.control === 'yes';

  // PASS: no foreign involvement
  if (allNo) {
    return { status: 'pass', reason: 'No foreign ownership, control, or influence identified.' };
  }

  // PASS: foreign involvement clearly described as simple, no high-risk indicators
  if (allAnswered && unsureCount === 0 && !highRiskYes && complexity === 'simple') {
    return { status: 'pass', reason: 'Foreign involvement identified and clearly described as manageable.' };
  }

  // FAIL: high-risk indicators without context clarity
  if (highRiskYes && !hasContext) {
    return { status: 'fail', reason: 'Significant FOCI indicators present without adequate complexity assessment.' };
  }

  // FAIL: ownership or control = yes without context
  if ((riskAnswers.ownership === 'yes' || riskAnswers.control === 'yes') && !hasContext) {
    return { status: 'fail', reason: 'Foreign ownership or control identified without FOCI complexity assessment.' };
  }

  // FAIL: complex or unsure complexity with missing answers
  if ((complexity === 'complex' || complexity === 'unsure') && !allAnswered) {
    return { status: 'fail', reason: 'Complex or unclear FOCI situation with incomplete supporting information.' };
  }

  // FAIL: complex situation with high-risk indicators
  if (complexity === 'complex' && highRiskYes) {
    return { status: 'fail', reason: 'Complex FOCI situation with significant foreign involvement indicators.' };
  }

  // REVIEW_REQUIRED: foreign involvement with context
  if (yesCount > 0 && hasContext) {
    return { status: 'review_required', reason: 'Foreign involvement identified. FOCI review recommended prior to DISP application.' };
  }

  // REVIEW_REQUIRED: unsure answers
  if (unsureCount > 0) {
    return { status: 'review_required', reason: 'Unclear FOCI status on some indicators. Clarification required.' };
  }

  // REVIEW_REQUIRED: remaining yes indicators
  if (yesCount > 0) {
    return { status: 'review_required', reason: 'Foreign involvement indicators require further assessment.' };
  }

  return { status: 'pass', reason: 'No significant FOCI concerns identified.' };
};

/**
 * Calculate complete assessment scores and status
 * Main entry point for scoring - now includes enhanced scores and FOCI gate
 * @param {Object} answers - All answers keyed by field_id
 * @returns {Object} Complete scoring results
 */
export const calculateAssessmentScores = (answers) => {
  const domainScores = calculateAllDomainScores(answers);
  const overallScore = calculateOverallScore(domainScores);
  const gatingStatus = getGatingControlStatus(answers);
  let overallStatus = determineOverallStatus(overallScore, gatingStatus);
  
  // Evaluate FOCI gate and apply readiness cap
  const fociGate = evaluateFOCIGate(answers);
  if (fociGate.status === 'fail') {
    if (overallStatus === 'ready_for_validation' || overallStatus === 'substantially_ready') {
      overallStatus = 'partially_ready';
    }
  } else if (fociGate.status === 'review_required') {
    if (overallStatus === 'ready_for_validation') {
      overallStatus = 'substantially_ready';
    }
  }

  // Add enhanced scoring
  const enhancedScores = calculateEnhancedScores(answers);

  return {
    domainScores,
    overallScore,
    overallStatus,
    gatingStatus,
    fociGate,
    readinessBand: getReadinessBandInfo(overallStatus),
    // Enhanced scoring additions
    enhanced: enhancedScores
  };
};

// ============================================================================
// TECHNOLOGY STACK PRESENCE & MATURITY ANALYSIS
// ============================================================================

/**
 * TOL field_id for each component
 */
const TOL_FIELD_MAP = {
  microsoft_entra_id: 'stack_microsoft_entra_id_status',
  conditional_access: 'stack_conditional_access_status',
  microsoft_intune: 'stack_microsoft_intune_status',
  microsoft_defender_for_endpoint: 'stack_microsoft_defender_for_endpoint_status',
  wdac_or_applocker: 'stack_wdac_or_applocker_status',
  tenable: 'stack_tenable_status',
  qualys: 'stack_qualys_status',
  microsoft_sentinel: 'stack_microsoft_sentinel_status',
  privileged_access_management: 'stack_privileged_access_management_status',
  backup_platform: 'stack_backup_platform_status',
  drata: 'stack_drata_status',
  remediation_tracker: 'stack_remediation_tracker_status',
  evidence_pack_generator: 'stack_evidence_pack_generator_status',
  disp_dashboard: 'stack_disp_dashboard_status',
};

/**
 * Check whether a capability field "passes" (score == 2)
 */
function fieldPasses(fieldId, answers) {
  const question = getQuestionByFieldId(fieldId);
  if (!question) return false;
  const answer = answers[fieldId];
  return getAnswerScore(answer, question) === 2;
}

/**
 * Get raw answer value for a field
 */
function getFieldValue(fieldId, answers) {
  const a = answers[fieldId];
  return a?.answer || a?.status || a?.value || null;
}

/**
 * Evaluate a single requirement condition
 */
function evaluateCondition(cond, answers) {
  const val = getFieldValue(cond.field, answers);
  if (cond.condition === 'not_pass') return !fieldPasses(cond.field, answers);
  if (cond.condition === 'eq') return val === cond.value;
  if (cond.condition === 'in') return cond.values?.includes(val);
  return false;
}

/**
 * Determine inferred_requirement for a component
 */
function inferRequirement(componentId, mapping, answers, priorityGaps) {
  // Check required_when conditions
  const requiredTriggered = mapping.required_when?.some(cond => evaluateCondition(cond, answers)) || false;

  // Also check if any priority gap references this component as required
  const gapRequired = priorityGaps?.some(g =>
    g.recommended_components?.includes(componentId) &&
    (g.priority === 'critical' || g.priority === 'high' || g.criticality === 'critical')
  ) || false;

  if (requiredTriggered || gapRequired) return 'required';

  const recommendedTriggered = mapping.recommended_when?.some(cond => evaluateCondition(cond, answers)) || false;
  const gapRecommended = priorityGaps?.some(g =>
    g.recommended_components?.includes(componentId) &&
    (g.priority === 'medium' || g.priority === 'low')
  ) || false;

  if (recommendedTriggered || gapRecommended) return 'recommended';

  return 'not_needed_yet';
}

/**
 * Infer tool status from existing direct questions when TOL answer is absent
 */
function inferFromDirectSignals(componentId, mapping, answers) {
  const directSignals = mapping.direct_signals || [];
  for (const fieldId of directSignals) {
    const val = getFieldValue(fieldId, answers);
    if (!val) continue;

    // For identity_platform
    if (fieldId === 'identity_platform') {
      if (val === 'microsoft_entra_id' || val === 'okta') return 'implemented_partial';
      if (val === 'on_prem_active_directory' || val === 'google_identity' || val === 'other') return 'implemented_limited';
      if (val === 'unknown') return 'unknown';
    }
    // For central_device_management
    if (fieldId === 'central_device_management') {
      if (val === 'intune' || val === 'sccm') return 'implemented_partial';
      if (val === 'other_mdm') return 'implemented_limited';
      if (val === 'none') return 'not_present';
    }
    // For logging_tool
    if (fieldId === 'logging_tool') {
      if (val === 'microsoft_sentinel' || val === 'splunk' || val === 'other_siem') return 'implemented_partial';
      if (val === 'native_logs_only') return 'implemented_limited';
      if (val === 'none') return 'not_present';
    }
    // For vulnerability_tool
    if (fieldId === 'vulnerability_tool') {
      if (val === 'tenable' && componentId === 'tenable') return 'implemented_partial';
      if (val === 'qualys' && componentId === 'qualys') return 'implemented_partial';
      if (val === 'microsoft_defender' && componentId === 'microsoft_defender_for_endpoint') return 'implemented_partial';
      if (val === 'other') return 'implemented_limited';
      if (val === 'none') return 'not_present';
    }
    // For evidence_collection_method
    if (fieldId === 'evidence_collection_method') {
      if (val === 'grc_platform') return 'implemented_partial';
      if (val === 'ticketing_system' || val === 'spreadsheet_tracking') return 'implemented_limited';
      if (val === 'manual_documents' || val === 'none') return 'not_present';
    }
    // For compliance_management_approach
    if (fieldId === 'compliance_management_approach') {
      if (val === 'integrated_grc') return 'implemented_partial';
      if (val === 'structured_manual' || val === 'spreadsheet_based') return 'implemented_limited';
      if (val === 'ad_hoc_reactive' || val === 'none') return 'not_present';
    }
    // For status-type fields (e.g. conditional_access, remediation_timelines_tracked)
    if (['conditional_access', 'remediation_timelines_tracked', 'vulnerabilities_tracked_and_remediated'].includes(fieldId)) {
      if (val === 'yes') return 'implemented_partial';
      if (val === 'partial') return 'implemented_limited';
      if (val === 'no') return 'not_present';
    }
    // For disp_status
    if (fieldId === 'disp_status') {
      if (val === 'current_member' || val === 'applying_within_3_months') return 'implemented_limited';
    }
    // For frameworks_currently_tracked (multiselect - presence indicates something)
    if (fieldId === 'frameworks_currently_tracked') {
      const selections = Array.isArray(val) ? val : [];
      if (selections.includes('disp') || selections.includes('essential_eight_ml2')) return 'implemented_limited';
    }
    // For os_patch_management_method
    if (fieldId === 'os_patch_management_method') {
      if (val === 'intune') return 'implemented_partial';
    }
  }
  return null;
}

/**
 * Normalize component effective_status using declared + capability signals
 */
function normalizeComponentStatus(componentId, declaredStatus, mapping, answers) {
  if (!declaredStatus || declaredStatus === 'not_present') return 'not_present';
  if (declaredStatus === 'planned_not_implemented') return 'planned_not_implemented';
  if (declaredStatus === 'implemented_limited') return 'implemented_limited';

  // For implemented_effective / implemented_partial, cross-check capability signals
  if (declaredStatus === 'implemented_effective' || declaredStatus === 'implemented_partial') {
    const capSignals = mapping.capability_signals || [];
    const criticalCapSignals = capSignals.filter(f => {
      const q = getQuestionByFieldId(f);
      return q && (q.criticality === 'critical' || q.criticality === 'high');
    });

    if (criticalCapSignals.length > 0) {
      const failingCritical = criticalCapSignals.filter(f => !fieldPasses(f, answers));
      if (declaredStatus === 'implemented_effective' && failingCritical.length > 0) {
        return 'present_but_ineffective';
      }
      if (declaredStatus === 'implemented_partial') {
        return failingCritical.length > criticalCapSignals.length / 2
          ? 'present_but_ineffective'
          : 'implemented_partial';
      }
    }
    return declaredStatus;
  }

  if (declaredStatus === 'unknown') return 'unknown';
  return declaredStatus;
}

/**
 * Determine confidence level for a component analysis
 */
function determineConfidence(hasTolAnswer, mapping, answers) {
  if (!hasTolAnswer) return 'low';

  const directSignals = mapping.direct_signals || [];
  const capSignals = mapping.capability_signals || [];
  const hasDirectCorroboration = directSignals.some(f => getFieldValue(f, answers) !== null);
  const hasCapCorroboration = capSignals.some(f => getFieldValue(f, answers) !== null);

  if (hasDirectCorroboration && hasCapCorroboration) return 'high';
  if (hasDirectCorroboration || hasCapCorroboration) return 'medium';
  return 'medium'; // TOL answer alone = medium
}

/**
 * Generate action text for a component based on its status and requirement
 */
function generateAction(componentId, effectiveStatus, requirement) {
  const label = COMPONENT_LABELS[componentId] || componentId;

  // Specific action descriptions per component
  const componentActions = {
    microsoft_entra_id: 'Enforce MFA for privileged users and strengthen identity controls',
    conditional_access: 'Configure context-aware access policies for device compliance and location',
    microsoft_intune: 'Deploy endpoint compliance policies and enable remote configuration',
    microsoft_defender_for_endpoint: 'Enable EDR and application execution monitoring',
    wdac_or_applocker: 'Implement application whitelisting enforcement',
    tenable: 'Deploy continuous vulnerability scanning',
    qualys: 'Deploy vulnerability scanning and compliance checks',
    microsoft_sentinel: 'Implement centralised log aggregation and alert monitoring',
    privileged_access_management: 'Implement PAM with just-in-time privilege elevation',
    backup_platform: 'Ensure immutable backups and regular restore testing',
    drata: 'Introduce structured compliance automation and control mapping',
    remediation_tracker: 'Establish remediation tracking and SLA enforcement',
    evidence_pack_generator: 'Implement automated evidence packaging for audit readiness',
    disp_dashboard: 'Deploy DISP submission tracking and CSQ alignment dashboard',
  };

  const displayStatus = DISPLAY_STATUS_MAP[effectiveStatus] || 'planned';

  if (displayStatus === 'implemented') {
    return 'Maintain current implementation';
  }
  if (displayStatus === 'needs_improvement') {
    return `Not meeting ML2 requirements - ${componentActions[componentId] || `gaps in ${label} enforcement or configuration`}`;
  }
  if (displayStatus === 'partial') {
    return `Partially deployed - ${componentActions[componentId] || `not consistently enforced across environment`}`;
  }
  if (displayStatus === 'missing') {
    if (requirement === 'required') {
      return `Not implemented - ${componentActions[componentId] || `required for ML2 compliance`}`;
    }
    if (requirement === 'recommended') {
      return `Not implemented - ${componentActions[componentId] || `recommended for maturity improvement`}`;
    }
    return `Not implemented - ${componentActions[componentId] || `deploy and configure`}`;
  }
  if (displayStatus === 'planned') {
    return `Planned but not yet active - ${componentActions[componentId] || `proceed with deployment`}`;
  }
  return componentActions[componentId] || `Review ${label} implementation status`;
}

/**
 * Build complete stack analysis from answers and scoring results
 * This is the main entry point for the Technology Stack Presence & Maturity layer
 * @param {Object} answers - All answers
 * @param {Array} priorityGaps - From generatePriorityGaps
 * @returns {Object} Complete stack analysis
 */
export const buildStackAnalysis = (answers, priorityGaps = []) => {
  const components = [];
  const summary = {
    implemented: 0,
    needs_improvement: 0,
    partial: 0,
    missing: 0,
    planned: 0,
    missing_required_count: 0,
    ineffective_required_count: 0,
    recommended_new_count: 0
  };
  const byLayer = {
    enforcement_layer: [],
    validation_layer: [],
    monitoring_layer: [],
    compliance_layer: []
  };

  // Track mutual exclusivity for Tenable/Qualys
  const mutualExclusiveResolved = {};

  Object.entries(STACK_COMPONENT_MAP).forEach(([componentId, mapping]) => {
    const tolFieldId = TOL_FIELD_MAP[componentId];
    const tolAnswer = answers[tolFieldId];
    const declaredStatus = tolAnswer?.answer || null;
    const hasTolAnswer = !!declaredStatus;

    // Determine declared or inferred status
    let rawStatus = declaredStatus;
    if (!rawStatus) {
      // Backward compat: infer from direct signals
      rawStatus = inferFromDirectSignals(componentId, mapping, answers);
    }

    // Normalize effective status
    const effectiveStatus = rawStatus
      ? normalizeComponentStatus(componentId, rawStatus, mapping, answers)
      : 'unknown';

    // Determine requirement
    let inferredRequirement = inferRequirement(componentId, mapping, answers, priorityGaps);

    // Handle mutual exclusivity for Tenable/Qualys
    if (mapping.mutually_exclusive_group?.length > 0) {
      const groupId = [componentId, ...mapping.mutually_exclusive_group].sort().join('_');
      if (inferredRequirement === 'required') {
        if (mutualExclusiveResolved[groupId]) {
          // The other one was already marked required, mark this as alternative
          inferredRequirement = 'alternative';
        } else {
          // Check if the other component is already effective
          const otherIds = mapping.mutually_exclusive_group;
          const otherEffective = otherIds.some(otherId => {
            const otherTol = TOL_FIELD_MAP[otherId];
            const otherVal = answers[otherTol]?.answer;
            return otherVal === 'implemented_effective' || otherVal === 'implemented_partial';
          });
          if (otherEffective) {
            inferredRequirement = 'alternative';
          } else {
            mutualExclusiveResolved[groupId] = componentId;
          }
        }
      }
    }

    // Confidence
    const confidence = determineConfidence(hasTolAnswer, mapping, answers);

    // Rationale for contradictions
    let rationale = '';
    if (effectiveStatus === 'present_but_ineffective' && (declaredStatus === 'implemented_effective' || declaredStatus === 'implemented_partial')) {
      rationale = 'Declared as implemented, but related control effectiveness remains weak.';
    }

    // Compute display status (normalized primary signal)
    const displayStatus = DISPLAY_STATUS_MAP[effectiveStatus] || 'planned';

    // Compute requirement category for display
    let requirementCategory = inferredRequirement;
    // Map not_needed_yet to 'sufficient' for display
    if (requirementCategory === 'not_needed_yet') requirementCategory = 'sufficient';

    // Action
    const action = generateAction(componentId, effectiveStatus, inferredRequirement);

    // Requirement text (secondary descriptive text)
    const requirementText = REQUIREMENT_TEXT[requirementCategory] || REQUIREMENT_TEXT[inferredRequirement] || '';

    // Build evidence signals
    const evidenceSignals = {
      direct_questions: (mapping.direct_signals || []).filter(f => getFieldValue(f, answers) !== null),
      capability_questions: (mapping.capability_signals || []).filter(f => getFieldValue(f, answers) !== null),
      corroborating_answers: (mapping.corroborating_fields || []).filter(f => getFieldValue(f, answers) !== null)
    };

    const record = {
      id: componentId,
      label: COMPONENT_LABELS[componentId] || componentId,
      layer_id: mapping.layer_id,
      declared_status: declaredStatus || null,
      inferred_requirement: inferredRequirement,
      requirement_category: requirementCategory,
      requirement_text: requirementText,
      effective_status: effectiveStatus,
      display_status: displayStatus,
      display_status_label: DISPLAY_STATUS_CONFIG[displayStatus]?.label || displayStatus,
      display_status_color: DISPLAY_STATUS_CONFIG[displayStatus]?.color || 'grey',
      confidence,
      evidence_signals: evidenceSignals,
      rationale,
      actions: [action],
      alternative_to: inferredRequirement === 'alternative' ? mapping.mutually_exclusive_group?.[0] || null : null
    };

    components.push(record);
    byLayer[mapping.layer_id]?.push(record);

    // Update summary using new normalized status model
    const statusKey = displayStatus;
    if (summary[statusKey] !== undefined) summary[statusKey]++;
    if (inferredRequirement === 'required' && (effectiveStatus === 'not_present' || effectiveStatus === 'planned_not_implemented' || effectiveStatus === 'unknown')) {
      summary.missing_required_count++;
    }
    if (inferredRequirement === 'required' && (effectiveStatus === 'present_but_ineffective' || effectiveStatus === 'implemented_limited')) {
      summary.ineffective_required_count++;
    }
    if (inferredRequirement === 'recommended' && (effectiveStatus === 'not_present' || effectiveStatus === 'planned_not_implemented')) {
      summary.recommended_new_count++;
    }
  });

  // Calculate stack_alignment_score (informational only, NOT part of ML2 score)
  const requiredComponents = components.filter(c => c.inferred_requirement === 'required');
  let alignmentScore = 0;
  if (requiredComponents.length > 0) {
    const totalPoints = requiredComponents.reduce((sum, c) => sum + (STACK_STATUS_SCORES[c.effective_status] || 0), 0);
    alignmentScore = Math.round(totalPoints / requiredComponents.length);
  }

  // Overall confidence
  const confidenceCounts = { high: 0, medium: 0, low: 0 };
  components.forEach(c => confidenceCounts[c.confidence]++);
  const overallConfidence = confidenceCounts.high >= components.length / 2 ? 'high'
    : confidenceCounts.low >= components.length / 2 ? 'low' : 'medium';

  // Generate summary text
  const summaryText = generateStackSummaryText(summary, alignmentScore);

  return {
    components,
    summary,
    by_layer: byLayer,
    stack_alignment_score: alignmentScore,
    overall_confidence: overallConfidence,
    summary_text: summaryText
  };
};

function generateStackSummaryText(summary, alignmentScore) {
  const parts = [];
  if (summary.implemented > 0) parts.push(`${summary.implemented} component${summary.implemented > 1 ? 's' : ''} implemented`);
  if (summary.needs_improvement > 0) parts.push(`${summary.needs_improvement} need${summary.needs_improvement > 1 ? '' : 's'} improvement`);
  if (summary.partial > 0) parts.push(`${summary.partial} partially implemented`);
  if (summary.missing > 0) parts.push(`${summary.missing} missing`);
  if (summary.planned > 0) parts.push(`${summary.planned} planned`);
  return parts.join('. ') + (parts.length > 0 ? '.' : 'No stack analysis available.');
}

export default {
  statusToScore,
  getAnswerScore,
  calculateWeightedScore,
  calculateDomainScore,
  calculateAllDomainScores,
  calculateOverallScore,
  getGatingControlStatus,
  determineOverallStatus,
  getDomainMaturityBand,
  getReadinessBandInfo,
  calculateAssessmentScores,
  evaluateFOCIGate,
  // Enhanced scoring exports
  SECTION_GROUPS,
  ENHANCED_SCORE_BANDS,
  AUDIT_LIKELIHOOD_MESSAGES,
  calculateGroupScore,
  calculateAllGroupScores,
  calculateBaseScore,
  getPolicyEnforcementMultiplier,
  getEvidenceMultiplier,
  countUnknownAnswers,
  getConfidenceMultiplier,
  detectScoringFlags,
  getEnhancedScoreBand,
  calculateAuditFailureLikelihood,
  calculateEnhancedScores,
  buildStackAnalysis
};
